/**
 * The APIs in this module are mostly implementation details of the router
 * itself, and not particuarly interesting with regards to authoring product
 * code. They currently adhere to the Suspense model of throwing async tasks
 * but will likely change over time to use properly "blessed" APIs of React.
 */

import client from '@mc/networking/http';
import { setPerformanceMarker } from '@mc/perfUtils';

const flagCheck = () => {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  return Boolean((window as any).gm_hp_lazy_marker).valueOf();
};

// A naive hashing function. Returns the identity of a given primitive,
// falling back to stringifying object types and others. Practically
// speaking, we don't need to account for non-primitive types in any
// deeper way, as the general inputs for the purposes of this module
// will be string | undefined.
function hashFn(obj: $TSFixMe) {
  switch (typeof obj) {
    case 'undefined':
    case 'boolean':
    case 'number':
    case 'string':
    case 'bigint':
    case 'symbol':
      return obj;
    default:
      return JSON.stringify(obj);
  }
}

function identity(v: $TSFixMe) {
  return v;
}

/**
 * `SuspenseRouteResource` objects are long-lived instances that exist
 * per route configuration. We use this as a mechanism to group all
 * code-splitting and page-data-fetching into a single, shared path.
 * This allows us to expose preloading/prefetching APIs that share the
 * same, underlying resources as our lazy, render-based variants (e.g.
 * how `RouteRenderer` works).
 *
 * As such, there's some logic here to allow for "caching" based on the
 * given inputs. In the case of code-splitting, there are none–it's all
 * handled statically by webpack at build time. In the case of fetching
 * data, we supply the `read` and `load` calls with the search string;
 * the reason for doing so is that, in many cases throughout our app,
 * we rely on query strings to provide ids and other required route
 * parameters. This allows for nicer interop with our existing React
 * screens, which always have a `getPageData` method supplied by their
 * controllers.
 */
class SuspenseRouteResource {
  error: $TSFixMe;
  getter: $TSFixMe;
  hash: $TSFixMe;
  isPending: $TSFixMe;
  promise: $TSFixMe;
  resolver: $TSFixMe;
  value: $TSFixMe;
  constructor(resolver: $TSFixMe, getter = identity) {
    this.resolver = resolver;
    this.getter = getter;
    this.hash = null;
    this.promise = null;
    this.value = null;
    this.error = null;
    this.isPending = false;
  }

  // Calls a resolver with the given input. Hashes the input in order
  // to invalidate any potential races or mismatches.
  load(resourceIdentifier: $TSFixMe) {
    const hash = hashFn(resourceIdentifier);

    if (this.promise === null || this.hash !== hash) {
      this.hash = hash;
      this.promise = this.resolver(resourceIdentifier);
      this.value = null;
      this.error = null;
      this.isPending = true;

      this.promise
        .then((value: $TSFixMe) => {
          if (this.hash === hash) {
            this.isPending = false;
            this.value = this.getter(value);
          }
          return value;
        })
        .catch((error: $TSFixMe) => {
          if (this.hash === hash) {
            this.isPending = false;
            this.error = error;
          }
          throw error;
        });
    }
  }

  // There are times where we no longer want to cache values, so
  // this method is for clearing them out. An example of where we
  // want to do this is for fetching data for a given route–we don't
  // have any centralized stores, so we shouldn't rely on long-lived
  // caches for data.
  flush() {
    if (this.isPending) {
      return;
    }

    this.hash = null;
    this.promise = null;
    this.value = null;
    this.error = null;
  }

  // Use the Suspense API of throwing a promise when wanting
  // to suspend UI on behalf of a resolvable.
  read(resourceIdentifier: $TSFixMe) {
    const hash = hashFn(resourceIdentifier);

    if (this.promise === null || this.hash !== hash) {
      this.load(resourceIdentifier);
    }

    if (this.value) {
      return this.value;
    } else if (this.error) {
      throw this.error;
    } else {
      throw this.promise;
    }
  }
}

// A factory for creating page-data-specific instances of the
// `SuspenseRouteResource` class. This incantation is used in
// a handful of places, most notably in our route preloading
// APIs, but also in our internal `Route` component (when we
// might lazily fetch missing page data). There, we also flush
// any data when unmounting active routes.
//
// The page data wrapper also provides a custom header in order
// to allow page data requests to signal a route redirect. We
// do this in order to provide a nice graduation path for any
// controllers which gate their actions behind a `preRun` method
// which might result in calls to `$this->_redirect()`.
//
// Additionally, handle the case where a page-data endpoint opts
// to throw a 404 by resolving with a sentinel value.
function createPageData(path: $TSFixMe, handle500statusCode?: boolean) {
  if (flagCheck() && path.includes('app-homepage/lazy')) {
    setPerformanceMarker('hp-lazy-s');
  }

  return new SuspenseRouteResource((search = '') => {
    // In the event that the given path already contains a search
    // string, we can merge the two together.
    if (path.includes('?')) {
      search = search.replace('?', '&');
    }
    return client
      .get(path + search, {
        headers: {
          'X-Spa-Redirect': 'on',
        },
      })
      .then(async (res: $TSFixMe) => {
        if (flagCheck() && path.includes('app-homepage/lazy')) {
          setPerformanceMarker('hp-lazy-e');
        }

        if (res.status === 404) {
          return {
            __404: true,
          };
        }

        if (handle500statusCode && res.status === 500) {
          try {
            const errorMessage = await res.json();
            return {
              __500: true,
              errorMessage,
            };
          } catch (e) {
            return {
              __500: true,
              errorMessage: res.statusText,
            };
          }
        }

        // When we get a 403 from fetching page data, we do a
        // reload of the page to try and recover the app.
        // A 403 that's not caught here will therwise show an
        // error and break the app.
        // A reload will either send users to /login if their
        // session is expired or will bring them back to where
        // they were navigating to.
        if (res.status === 403) {
          window.location.reload();
          return false;
        }

        return res.json();
      });
  });
}

// A page-data wrapper for autolyse functions.
// This API allows us to side-step any hand-crafted reconcilation of data
// from services by utilizing the autolyse tooling.
//
// Again, we rely upon the "suspense"-y model of throwing thenables
// here, while also accounting for the single-page app's sentinel
// __404 value when a resource responds with such. Errors are not
// handled, other than showing a generic error screen (and logging
// the error in the console/Bugsnag).
function createPageDataFromAutolyseResolver(thenable: $TSFixMe) {
  return new SuspenseRouteResource((search: $TSFixMe) => {
    const params = {};
    const searchParams = new URLSearchParams(search);

    for (const [key, value] of searchParams) {
      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      params[key] = value;
    }

    return thenable(params, {
      headers: {
        'X-Spa-Redirect': 'on',
      },
    }).catch((json: $TSFixMe) => {
      if (json.code === 404) {
        return {
          __404: true,
        };
      }

      // When we get a 403 from fetching page data, we do a
      // reload of the page to try and recover the app.
      // A 403 that's not caught here will otherwise show an
      // error and break the app.
      // A reload will either send users to /login if their
      // session is expired or will bring them back to where
      // they were navigating to.
      if (
        json.code === 'permission_denied' &&
        json.meta?.details === '3a321b3f'
      ) {
        window.location.reload();
        return false;
      }

      throw json;
    });
  });
}

// Handles fetching components for routing. Entirely persistent.
function createSplitRoute(importFn: $TSFixMe) {
  return new SuspenseRouteResource(importFn, (mod) => mod.default);
}

export { createSplitRoute, createPageData, createPageDataFromAutolyseResolver };
